import React, { useEffect, useRef, useState } from 'react';
import { Camera, X, SwitchCamera } from 'lucide-react';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';

const READER_ID = 'camera-barcode-reader';

const CameraBarcodeScanner = ({ onScan, onClose }) => {
  const scannerRef = useRef(null);
  const [error, setError] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [activeCameraIdx, setActiveCameraIdx] = useState(0);
  const startedRef = useRef(false);

  useEffect(() => {
    let mounted = true;

    const init = async () => {
      try {
        const devices = await Html5Qrcode.getCameras();
        if (!mounted) return;
        if (!devices || devices.length === 0) {
          setError('No camera found on this device');
          return;
        }
        setCameras(devices);

        // Prefer back camera
        const backIdx = devices.findIndex(d =>
          d.label.toLowerCase().includes('back') ||
          d.label.toLowerCase().includes('rear') ||
          d.label.toLowerCase().includes('environment')
        );
        const idx = backIdx >= 0 ? backIdx : 0;
        setActiveCameraIdx(idx);

        await startScanner(devices[idx].id);
      } catch (err) {
        if (mounted) {
          setError('Camera access denied. Please allow camera permission.');
        }
      }
    };

    init();

    return () => {
      mounted = false;
      stopScanner();
    };
  }, []);

  const startScanner = async (cameraId) => {
    await stopScanner();

    const scanner = new Html5Qrcode(READER_ID);
    scannerRef.current = scanner;

    try {
      await scanner.start(
        cameraId,
        { fps: 10, qrbox: { width: 220, height: 120 }, aspectRatio: 1.5 },
        (decodedText) => {
          if (decodedText && decodedText.length > 3) {
            onScan(decodedText);
            toast.success(`Scanned: ${decodedText}`);
          }
        },
        () => {}
      );
      startedRef.current = true;
    } catch (err) {
      setError('Failed to start camera: ' + (err?.message || err));
    }
  };

  const stopScanner = async () => {
    if (scannerRef.current && startedRef.current) {
      try {
        await scannerRef.current.stop();
        startedRef.current = false;
      } catch {}
    }
    scannerRef.current = null;
  };

  const handleClose = async () => {
    await stopScanner();
    onClose();
  };

  const switchCamera = async () => {
    if (cameras.length < 2) return;
    const nextIdx = (activeCameraIdx + 1) % cameras.length;
    setActiveCameraIdx(nextIdx);
    await startScanner(cameras[nextIdx].id);
  };

  return (
    <div className="bg-white border-2 border-[#1e3a5f] shadow-lg" data-testid="camera-scanner">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-[#1e3a5f] text-white">
        <div className="flex items-center gap-2">
          <Camera className="w-4 h-4" />
          <span className="text-sm font-medium">Camera Scanner</span>
        </div>
        <div className="flex items-center gap-1">
          {cameras.length > 1 && (
            <button
              onClick={switchCamera}
              className="p-1.5 hover:bg-white/20 transition-colors"
              title="Switch camera"
              data-testid="camera-switch-btn"
            >
              <SwitchCamera className="w-4 h-4" />
            </button>
          )}
          <button
            onClick={handleClose}
            className="p-1.5 hover:bg-white/20 transition-colors"
            data-testid="camera-close-btn"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Camera Feed */}
      <div className="relative" style={{ minHeight: '200px' }}>
        {error ? (
          <div className="flex items-center justify-center h-[200px] bg-slate-100 px-4">
            <p className="text-sm text-red-600 text-center">{error}</p>
          </div>
        ) : (
          <div id={READER_ID} style={{ width: '100%' }} />
        )}
      </div>

      {/* Footer hint */}
      <div className="px-3 py-2 bg-slate-50 border-t border-slate-100">
        <p className="text-xs text-slate-500 text-center">
          Point camera at a barcode to scan
        </p>
      </div>
    </div>
  );
};

export default CameraBarcodeScanner;
